/**
 * Cabinet report — helpers partagés (format, sélecteurs, libellés famille).
 *
 * Source de vérité pour les libellés famille côté frontend, alignée sur
 * `backend/services/report_constants.py:FAMILY_LABELS`. À garder synchronisée.
 */

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

export function formatPeriod(period) {
  if (!period) return '—';
  const [y, m] = String(period).split('-');
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return period;
  return `${MONTHS_FR[idx]} ${y}`;
}

/** Find a generated section in a report by its `kind` (matches PROMPT_SECTION_KEYS). */
export function getSection(report, kind) {
  if (!report?.cabinet_sections) return null;
  return report.cabinet_sections.find((s) => s.kind === kind);
}

/** Filter `report.kpi_results` by family. */
export function getKpisByFamily(report, family) {
  if (!report?.kpi_results) return [];
  return report.kpi_results.filter((k) => k.family === family);
}

export const FAMILY_LABELS = {
  activite: 'Activité commerciale',
  rentabilite: 'Rentabilité',
  stock: 'Stock & trésorerie',
  patientele: 'Patientèle & services',
};

export const FAMILY_BASELINES = {
  activite: "Le flux d'affaires et la dynamique de votre comptoir.",
  rentabilite: 'Les marges et la valeur économique dégagée.',
  stock: 'Les flux financiers, le tiers payant et la tenue du fonds.',
  patientele: "La fidélité, la solidité de l'ordonnance et le mix conseil.",
};

/** Mapping famille → kind de section (côté backend report_prompts). */
export const FAMILY_TO_SECTION_KIND = {
  activite: 'family_activite',
  rentabilite: 'family_rentabilite',
  stock: 'family_stock',
  patientele: 'family_patientele',
};

/** 3 paliers de performance pour la couleur d'un KPI selon son percentile. */
export function performanceTier(percentile) {
  if (percentile == null) return 'neutral';
  if (percentile >= 60) return 'good';
  if (percentile >= 40) return 'average';
  return 'weak';
}

/** Couleur cabinet associée à un palier de performance (hex pour SVG). */
export const TIER_COLOR_HEX = {
  good: '#4A6B3A',     // cabinet-forest
  average: '#B5862A',  // cabinet-gold
  weak: '#8B0028',     // cabinet-bordeaux
  neutral: '#9A8E80',  // cabinet-ink-soft
};

/** Classe Tailwind pour la couleur d'accent d'un KPI selon son palier. */
export const TIER_BORDER_CLASS = {
  good: 'border-l-cabinet-forest',
  average: 'border-l-cabinet-gold',
  weak: 'border-l-cabinet-bordeaux',
  neutral: 'border-l-cabinet-ink-soft',
};

/** Format usuel d'une valeur de KPI selon son type/unité.
 *  Aligné avec services/fr_formatters.py. */
export function formatKpiValue(kpi, formatters) {
  if (!kpi || kpi.value == null) return '—';
  const ft = kpi.format_type;
  if (ft === 'percentage' || kpi.unit === '%') return formatters.formatPercentageFr(kpi.value, 1);
  if (kpi.unit === '€' || kpi.unit === '€/ETP') return formatters.formatCurrencyFr(kpi.value, 0);
  if (kpi.unit === 'jours') return formatters.formatDaysFr(kpi.value);
  return formatters.formatNumberFr(kpi.value, ft === 'decimal' ? 1 : 0);
}
